// File: /opt/stacks/workout/app/src/contexts/WorkoutContext.js

import React, {
  createContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import useLocalStorage from "../hooks/useLocalStorage";
// import ollamaApi from "../apis/ollamaApi";
import llmApi from "../apis/llmApi";
import { accumulateUsage, formatUsageSummary } from "../apis/llm/usage";
import { recordUsage } from "../apis/llm/usageLog";
import { accumulateDiagnostics } from "../apis/llm/diagnostics";
import api from "../services/api";
import useSound from "../hooks/useSound";
import {
  getAudioSettings,
  mergeAudioSettingsFromServer,
  audioSettingsToApi,
} from "../hooks/useAudioSettings";
import {
  getWorkoutSettings,
  mergeWorkoutSettingsFromServer,
  workoutSettingsToApi,
} from "../hooks/useWorkoutSettings";
import {
  getDebugSettings,
  mergeDebugSettingsFromServer,
  debugSettingsToApi,
} from "../hooks/useDebugSettings";
import useWorkoutCoach from "../hooks/useWorkoutCoach";
import useSessionRecipes from "../hooks/useSessionRecipes";
import { expandRecipeConfig } from "../utils/sessionRecipes";
import { buildStandaloneSessionPlan } from "../utils/devicePlan";
import {
  DEFAULT_LLM_SETTINGS,
  llmSettingsToApi,
  mergeFetchedLlmSettings,
  shouldSyncLlmSettingsToServer,
} from "../utils/llmSettings";
import {
  DEFAULT_USER_PROFILE,
  resolveSegmentMode,
  segmentFlagsForMode,
} from "../utils/workoutPlanUtils";
import { buildCarryOverRecord } from "../utils/generationCarryOver";
import { summariseTrainingHistory } from "../utils/trainingHistory";
import {
  resolveMixedStyle,
  hasLoadEquipment,
  readLastArchetypeId,
  rememberArchetypeId,
} from "../utils/mixedStyle";
import { weeklyMuscleSets as countWeeklyMuscleSets } from "../utils/muscleVolume";
import {
  normaliseStyleSelection,
  resolveSessionFormat,
} from "../utils/sessionFormat";
import { coerceLegacyNodeTypes } from "../utils/planNormaliser";
import {
  toApiProfile,
  fromApiProfile,
  mergeLocalProfileWithCloud,
  profilesAreEquivalent,
  profileNeedsServerSync,
} from "../utils/profileSync";
import { useAuth } from "./AuthContext";
import { useUserProfile } from "./UserProfileContext";
import { computeWeeklyTargets } from "../utils/weeklyTargets";
import useExerciseHistory from "../hooks/useExerciseHistory";
import { useToast } from "./ToastContext";
import { heartRateBasis, raisedMaxHr, sustainedPeakHr, zoneFloors } from "../utils/heartRateZones";
import { ZoneFloorsContext } from "./ZoneFloorsContext";
import { WeightUnitContext } from "./WeightUnitContext";
import { setShownWeightUnit, weightUnitOf } from "../utils/units";

export const WorkoutContext = createContext();
export const AppStateContext = createContext();
export const TimerContext = createContext();

// --- Consumer Hooks ---
// Targeted hooks for components that only need one context slice.
// Components that need both should import both hooks.
export const useAppState = () => React.useContext(AppStateContext);
export const useTimer = () => React.useContext(TimerContext);

const payloadDiffersFromServer = (payload, serverSettings = {}) =>
  Object.entries(payload).some(([key, value]) => {
    const serverValue = serverSettings[key] ?? null;
    return JSON.stringify(value ?? null) !== JSON.stringify(serverValue);
  });

// Map a raw generation error into a short, human-friendly toast description.
// Provider errors arrive as deeply-nested JSON strings (e.g. Google wraps a
// 503 "UNAVAILABLE" payload inside its message), which is useless to a user —
// so we sniff for the common transient cases and fall back to the raw message.
const describeGenerationError = (error) => {
  const raw = (error?.message || "").toLowerCase();

  if (
    raw.includes("503") ||
    raw.includes("unavailable") ||
    raw.includes("overloaded") ||
    raw.includes("high demand")
  ) {
    return "The AI model is busy right now (high demand). Please try again in a moment, or switch provider/model in Settings.";
  }
  // Daily quota / free-tier cap (e.g. Google's "...PerDay...FreeTier") won't
  // clear on a short retry — match the daily-specific signals only, so we can
  // give different advice from a transient per-minute rate limit. (The generic
  // "RESOURCE_EXHAUSTED"/"billing" strings appear on per-minute limits too, so
  // they're deliberately not used here.)
  if (
    raw.includes("free_tier") ||
    raw.includes("free tier") ||
    raw.includes("perday") ||
    raw.includes("per day")
  ) {
    return "Daily free-tier limit reached for this model. It won't reset until tomorrow — add billing or switch provider/model in Settings.";
  }
  if (
    raw.includes("429") ||
    raw.includes("too many requests") ||
    raw.includes("rate limit") ||
    raw.includes("quota") ||
    raw.includes("resource_exhausted")
  ) {
    return "Hit the provider's rate limit. Wait a minute and try again, or switch provider/model in Settings.";
  }
  if (
    raw.includes("invalid api key") ||
    raw.includes("authentication") ||
    raw.includes("unauthorized") ||
    raw.includes("401") ||
    raw.includes("403")
  ) {
    return "The provider rejected your API key. Check it in Settings and try again.";
  }
  // Every attempt hit the per-call time limit (#167).
  if (raw.includes("sent nothing for") || raw.includes("took longer than")) {
    return "The AI didn't answer in time, even after trying again. Try again, or pick a faster model or a lower Thinking level in Settings.";
  }
  if (raw.includes("truncat")) {
    return "The model's response was cut off before completing. Try again, or pick a model with a larger output limit.";
  }
  if (raw.includes("failed to fetch") || raw.includes("networkerror")) {
    return "Couldn't reach the AI provider. Check your connection (or that the provider proxy is running) and try again.";
  }
  return error?.message || "An unknown error occurred while generating your session.";
};

export const WorkoutProvider = ({ children }) => {
  const [userProfile, setUserProfile] = useLocalStorage(
    "userProfile",
    DEFAULT_USER_PROFILE,
  );
  const [workoutPlan, setWorkoutPlan] = useLocalStorage("workoutPlan", null);
  // One-shot legacy-type coercion: rewrites any persisted `SetRest` nodes in
  // localStorage to `Rest` so downstream code can assume a single recovery
  // type. Runs at mount; cheap to repeat if hot-reload swaps the provider.
  useEffect(() => {
    if (!workoutPlan) return;
    const coerced = coerceLegacyNodeTypes(workoutPlan);
    if (coerced !== workoutPlan) setWorkoutPlan(coerced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [view, setView] = useLocalStorage("appView", "session");
  const [planSubView, setPlanSubView] = useState("overview");
  // A sheet the Settings view should open on arrival. Lets deep-links (e.g.
  // "AI Settings" from the generate dialog) land on the right sheet; Settings
  // clears it once opened.
  const [settingsSheet, setSettingsSheet] = useState(null);
  // Same idea for Social: a notification about a mate request has to land on
  // the Mates tab, not on the feed with the news one tap out of sight.
  const [socialTab, setSocialTab] = useState("feed");
  const [isImmersive, setIsImmersive] = useState(false);
  const [error, setError] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [thinkingText, setThinkingText] = useState(null);
  // Set while the refinement call runs (#166): taking it ends the wait and
  // keeps the first plan.
  const [takeFirstPlan, setTakeFirstPlan] = useState(null);
  // Token/cost accounting for the most recent generation (issue #71). Survives
  // the generation finishing so the Session view can show what it cost.
  const [lastGenerationUsage, setLastGenerationUsage] = useState(null);
  // The dialog answers behind the plan on screen, so "Regenerate" reopens the
  // form as it was submitted instead of blank. Persisted next to `workoutPlan`
  // (which is also localStorage-backed) so a reload keeps the two in step, and
  // keyed by plan id so it is only ever offered for the plan it produced.
  const [lastGenerationConfig, setLastGenerationConfig] = useLocalStorage(
    "lastGenerationConfig",
    null,
  );
  const abortControllerRef = useRef(null);
  const playSound = useSound();

  // Auth integration - sync profile when user logs in
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  // Set once loadPresetIntoCoach exists, for callbacks declared above it.
  const loadPresetRef = useRef(null);
  // Set below, once the profile setter exists; the coach calls it after a save.
  const notePeakHrRef = useRef(null);

  // Lifted above useWorkoutCoach: the engine compiles its queue at
  // construction and needs the rep-estimate inputs by then.
  const {
    profile: cloudProfile,
    hasLoadedUserData,
    syncProfile,
    updateProfile: updateCloudProfile,
    locationPresets,
    equipmentPresets,
    performanceCapabilities,
    sessions,
  } = useUserProfile();

  // Per-exercise logged pace, blended into the rep estimator once there are
  // enough samples. A pure useMemo over the profile's sessions.
  const exerciseHistoryMap = useExerciseHistory();

  // All guided-session execution lives inside useWorkoutCoach, which owns the
  // compiled-queue WorkoutEngine. WorkoutContext just wires the hook's outputs
  // into the three consumer contexts below.
  const coach = useWorkoutCoach({
    workoutPlan,
    setWorkoutPlan,
    playSound,
    isAuthenticated,
    setView,
    showToast,
    // Rep-estimate inputs for the compiled queue. useExerciseHistory's own
    // header describes this as consumed "via WorkoutContext" — it reached the
    // session card and the remaining-time maths, but never the engine itself.
    profile: cloudProfile,
    historyMap: exerciseHistoryMap,
    notePeakHrRef,
  });
  const {
    coachState,
    setCoachState,
    sessionLoadedIntoCoach,
    setSessionLoadedIntoCoach,
    countInRemaining,
    countInTotal,
    countInContext,
    startWorkout,
    finishWorkout,
    completeWorkout,
    keepGoing,
    savePartialSession,
    playPause,
    skipToNext,
    skipToPrevious,
    confirmReady,
    skipRoundForward,
    skipRoundBack,
    getUpcomingExercises,
    getActiveLoadContext,
    updateActiveLoad,
    getLastRepsSet,
    getDoneRepsSets,
    amendSetReps,
    updateFreeLog,
    updateFreeChecklist,
    updateFreeRests,
    applySessionEdit,
    getTrimContext,
    loadSessionIntoCoach,
    loadSessionFromData,
    clearLoadedSession,
    clearSession,
    setActiveProgrammeSession,
    activeProgrammeSessionRef,
    updatePlanNode,
    deletePlanNode,
    addPlanNode,
    movePlanNode,
    duplicatePlanNode,
    planDisplayProps,
    bikeSummaryRef,
    machinesRef,
    telemetryRef,
    telemetrySummaryRef,
    telemetrySessionIdRef,
    telemetryFinalizeRef,
    telemetryClearAfterSaveRef,
    telemetryPeekRef,
  } = coach;

  // NEW: State for LLM Settings (with per-provider key storage)
  const [llmSettingsLocal, setLlmSettingsLocal] = useLocalStorage(
    "llmSettings",
    DEFAULT_LLM_SETTINGS,
  );
  const llmSettingsLocalRef = useRef(llmSettingsLocal);
  llmSettingsLocalRef.current = llmSettingsLocal;

  const setLlmSettings = useCallback(
    (newSettings) => {
      const apiKeys = {
        ...(newSettings.apiKeys || llmSettingsLocal.apiKeys || {}),
      };

      if (newSettings.apiKey && newSettings.provider !== "ollama") {
        apiKeys[newSettings.provider] = newSettings.apiKey;
      }

      const updated = {
        ...DEFAULT_LLM_SETTINGS,
        ...newSettings,
        apiKeys,
        apiKey:
          newSettings.provider === "ollama"
            ? ""
            : newSettings.apiKey || apiKeys[newSettings.provider] || "",
      };

      setLlmSettingsLocal(updated);

      if (isAuthenticated) {
        api.updateSettings(llmSettingsToApi(updated)).catch(console.error);
      }
    },
    [isAuthenticated, setLlmSettingsLocal, llmSettingsLocal.apiKeys],
  );

  // Sync settings when authenticated
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let isActive = true;

    api
      .getSettings()
      .then((dbSettings) => {
        if (!isActive) return;

        const localSettings = llmSettingsLocalRef.current;
        const fetchedSettings = dbSettings || {};
        const mergedSettings = mergeFetchedLlmSettings(
          localSettings,
          fetchedSettings,
        );
        const mergedAudioSettings =
          mergeAudioSettingsFromServer(fetchedSettings) || getAudioSettings();
        const mergedWorkoutSettings =
          mergeWorkoutSettingsFromServer(fetchedSettings) ||
          getWorkoutSettings();
        const mergedDebugSettings =
          mergeDebugSettingsFromServer(fetchedSettings) || getDebugSettings();

        setLlmSettingsLocal(mergedSettings);

        const syncPayload = {};

        if (shouldSyncLlmSettingsToServer(localSettings, fetchedSettings)) {
          Object.assign(syncPayload, llmSettingsToApi(mergedSettings));
        }

        const audioPayload = audioSettingsToApi(mergedAudioSettings);
        if (payloadDiffersFromServer(audioPayload, fetchedSettings)) {
          Object.assign(syncPayload, audioPayload);
        }

        const workoutPayload = workoutSettingsToApi(mergedWorkoutSettings);
        if (payloadDiffersFromServer(workoutPayload, fetchedSettings)) {
          Object.assign(syncPayload, workoutPayload);
        }

        const debugPayload = debugSettingsToApi(mergedDebugSettings);
        if (payloadDiffersFromServer(debugPayload, fetchedSettings)) {
          Object.assign(syncPayload, debugPayload);
        }

        if (Object.keys(syncPayload).length > 0) {
          api.updateSettings(syncPayload).catch(console.error);
        }
      })
      .catch(console.error);

    return () => {
      isActive = false;
    };
  }, [isAuthenticated, setLlmSettingsLocal]);

  // Expose llmSettings as simple value (from local state)
  const llmSettings = llmSettingsLocal;

  // Today's US$ → A$ rate, for showing costs in A$ (utils/money.js). Kept on
  // the device so the last rate shows straight away and offline.
  const [fxUsdAud, setFxUsdAud] = useLocalStorage("fxUsdAud", null);
  useEffect(() => {
    api
      .getFxRate()
      .then((res) => {
        if (res?.usdAud?.rate) setFxUsdAud({ rate: res.usdAud.rate, date: res.usdAud.date });
      })
      .catch(() => {});
  }, [setFxUsdAud]);
  const money = useMemo(
    () => ({
      currency: llmSettings.currency || "AUD",
      rate: fxUsdAud?.rate ?? null,
      date: fxUsdAud?.date ?? null,
    }),
    [llmSettings.currency, fxUsdAud],
  );

  const { defaultRecipe, touchRecipe } = useSessionRecipes();

  // The latest local profile, and how many saves are on their way to the
  // server. The merge below runs when the cloud copy changes, not on every
  // local edit: re-merging on an edit let the older cloud copy win and flip
  // the edit back until the save's reply landed (the Units toggle jittered).
  const userProfileRef = useRef(userProfile);
  userProfileRef.current = userProfile;
  const pendingProfileSaves = useRef(0);

  // When user logs in, sync local profile to cloud
  useEffect(() => {
    if (!isAuthenticated || !hasLoadedUserData) return;
    const userProfile = userProfileRef.current;

    if (cloudProfile) {
      // A reply to an earlier save while a newer one is still on its way
      // is already out of date; the newer reply will settle it.
      if (pendingProfileSaves.current > 0) return;
      const mergedProfile = mergeLocalProfileWithCloud(
        userProfile,
        cloudProfile,
      );

      if (!profilesAreEquivalent(userProfile, mergedProfile)) {
        setUserProfile(mergedProfile);
      }

      if (profileNeedsServerSync(mergedProfile, cloudProfile)) {
        updateCloudProfile(toApiProfile(mergedProfile)).catch(console.error);
      }

      return;
    }

    if (userProfile) {
      syncProfile(toApiProfile(userProfile)).catch(console.error);
    }
  }, [
    cloudProfile,
    hasLoadedUserData,
    isAuthenticated,
    profilesAreEquivalent,
    syncProfile,
    updateCloudProfile,
  ]);

  // Sync to cloud when profile changes (debounced)
  const handleSetUserProfile = useCallback(
    (value) => {
      setUserProfile(value);

      // If authenticated, sync to cloud
      if (
        isAuthenticated &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        // Convert local format to API format
        const apiProfile = toApiProfile(value);
        pendingProfileSaves.current += 1;
        updateCloudProfile(apiProfile)
          .catch(console.error)
          .finally(() => {
            pendingProfileSaves.current -= 1;
          });
      }
    },
    [isAuthenticated, updateCloudProfile],
  );

  // A saved session whose strap held a higher heart rate than the max the
  // zones use raises the max to it (#187), and says so.
  const notePeakHeartRate = useCallback(
    (samples) => {
      const profile = userProfileRef.current;
      const next = raisedMaxHr(sustainedPeakHr(samples), heartRateBasis(profile));
      if (!next) return;
      handleSetUserProfile({ ...profile, maxHeartRate: next });
      showToast({
        title: `New max heart rate: ${next} bpm`,
        description: "Your heart rate zones now use it. You can change it in Profile.",
      });
    },
    [handleSetUserProfile, showToast],
  );
  notePeakHrRef.current = notePeakHeartRate;

  // App state should only be 'planning' while an actual generation is in progress.
  // On first open (no plan yet), remain 'ready' until the user clicks Generate.
  const appState = useMemo(() => {
    if (coachState.status === "planning") return "planning";
    // "readyGate" is an active sub-state (rep→timed handoff) — it must keep the
    // active coach view mounted, otherwise the session appears to end and falls
    // back to the idle/start screen.
    if (["running", "paused", "readyGate"].includes(coachState.status))
      return "active";
    if (coachState.status === "finished") return "finished";
    return "ready";
  }, [coachState.status]);

  // ---------------------------------------------------------------------------
  // Plan generation
  // ---------------------------------------------------------------------------

  const generatePlan = useCallback(
    async (planConfig) => {
      const profileNotes = userProfile.injuryNotes || "";
      const sessionNotes = planConfig.sessionNotes || "";
      const combinedNotes = [profileNotes, sessionNotes]
        .filter(Boolean)
        .join(". ");

      const {
        skipLastRest,
        defaultTransitionSeconds: defaultTransitionSecondsSetting,
        defaultBlockSetupSeconds: defaultBlockSetupSecondsSetting,
        guidedSessionEnabled,
        historyLookbackDays,
      } = getWorkoutSettings();

      // Intelligent session guidance (issue #9): summarise recent completed
      // sessions and feed them to the prompt, unless the user disabled it in
      // settings or toggled it off for this generation in the dialog.
      const useGuidance =
        guidedSessionEnabled && planConfig.useHistoryGuidance !== false;
      const recentTrainingHistory = useGuidance
        ? summariseTrainingHistory(sessions, {
            lookbackDays: historyLookbackDays,
          })
        : [];
      // Hard sets per muscle this week (#83 L1). Same opt-out as history.
      const weeklyMuscleSets = useGuidance ? countWeeklyMuscleSets(sessions) : null;

      // "Mixed" is not a shape the model can build — it is a request for one.
      // Resolving it here rather than inside buildPrompt keeps the prompt a pure
      // string builder, and means the chosen combination can be stamped on the
      // plan and carried into a regenerate.
      //
      // History feeds the rotation only when guidance is on: a user who turned
      // that off has asked us not to shape sessions from their history, and
      // rotating archetypes by it would do exactly that. With it off, the
      // previous archetype is still excluded, so shapes never repeat back to
      // back — they just stop being informed by what was trained.
      // The dialog previews this same resolution and passes the shape it
      // showed as mixedArchetypeId, so what the user saw is what gets built.
      // Goals and format arrive as two choices (#83 L4); legacy selections
      // that put a format in the goal list are moved across first.
      const selection = normaliseStyleSelection({
        workoutTypes: planConfig.workoutTypes,
        format: planConfig.format,
      });
      const mixedResolution = resolveMixedStyle(selection.workoutTypes, {
        history: recentTrainingHistory,
        heldArchetypeId: planConfig.mixedArchetypeId || null,
        excludeArchetypeId: readLastArchetypeId(),
        blockedArchetypeIds: userProfile.excludedSessionShapes || [],
        sessionMinutes: planConfig.sessionDuration,
        hasLoad: hasLoadEquipment(planConfig.availableEquipment),
        readiness: planConfig.readiness,
        preferences: {
          restStyle: userProfile.restStyle,
          sessionPace: userProfile.sessionPace,
        },
        muscleSets: weeklyMuscleSets,
        noFaff: Boolean(planConfig.noFaff),
        // What's next measures the week against this person's goal and days.
        targets: computeWeeklyTargets({
          userProfile,
          trainingDates: (sessions || []).map((s) => s.started_at),
        }).counts,
      });
      // A selector-chosen shape carries its own format; otherwise the user's
      // format (or Auto) decides how the goals are run.
      const formatResolution = resolveSessionFormat({
        goals: mixedResolution.styles,
        format: selection.format,
        noFaff: Boolean(planConfig.noFaff),
        selectorUsed: Boolean(mixedResolution.archetype),
      });

      // Resolve transition seconds for this generation:
      //   active location override (if any) → user default → fallback.
      // `planConfig.trainingLocation` is typically "user-<id>" for stored
      // presets, or a freeform string like "Home" for unauthenticated guests.
      const activeLocationId =
        typeof planConfig.trainingLocation === "string" &&
        planConfig.trainingLocation.startsWith("user-")
          ? Number(planConfig.trainingLocation.slice(5))
          : null;
      const activeLocation =
        activeLocationId != null
          ? (locationPresets || []).find((lp) => lp.id === activeLocationId)
          : null;
      const resolvedTransitionSeconds =
        activeLocation && activeLocation.transition_seconds != null
          ? Number(activeLocation.transition_seconds)
          : Number.isFinite(Number(defaultTransitionSecondsSetting))
            ? Number(defaultTransitionSecondsSetting)
            : 15;
      const resolvedBlockSetupSeconds =
        activeLocation && activeLocation.block_setup_seconds != null
          ? Number(activeLocation.block_setup_seconds)
          : Number.isFinite(Number(defaultBlockSetupSecondsSetting))
            ? Number(defaultBlockSetupSecondsSetting)
            : 45;

      const profile = {
        ...planConfig,
        skipLastRest,
        transitionSeconds: resolvedTransitionSeconds,
        blockSetupSeconds: resolvedBlockSetupSeconds,
        activeLocation: activeLocation
          ? {
              id: activeLocation.id,
              name: activeLocation.name,
              transitionSeconds: resolvedTransitionSeconds,
              blockSetupSeconds: resolvedBlockSetupSeconds,
            }
          : null,
        fitnessLevel: userProfile.fitnessLevel || "intermediate",
        trainingExperience: userProfile.trainingExperience || "intermediate",
        birthYear: userProfile.birthYear || null,
        injuryLimitationNotes: combinedNotes,
        // Standing preferences for how a session is put together. Read from
        // the profile, never planConfig: they apply across every style picked.
        // A Straight sets or Supersets format overrides the profile's rest
        // preference for this session only.
        restStyle: formatResolution.restStyleOverride || userProfile.restStyle,
        sessionPace: userProfile.sessionPace,
        trainingPreferences: userProfile.trainingPreferences || "",
        formatStyle: formatResolution.formatStyle,
        formatGoals: formatResolution.formatGoals,
        workoutStyle: formatResolution.styles[0] || "mixed",
        // All selected styles, so the prompt can compose a sequenced multi-block
        // session (e.g. cardio → strength → metcon finisher) rather than only using
        // the first pick. `workoutStyle` above stays the primary for back-compat.
        // Post-resolution, so "mixed" never reaches the prompt as a style.
        workoutStyles: formatResolution.styles,
        sessionShape: mixedResolution.archetype
          ? {
              name: mixedResolution.archetype.name,
              glue: mixedResolution.archetype.glue,
            }
          : null,
        // Warm-up/cool-down tri-state (issue #70) expanded into the flag pair the
        // prompt builder reads. Resolved rather than read straight off planConfig
        // so a preset saved before #70 still maps to a sensible mode.
        ...segmentFlagsForMode(
          resolveSegmentMode(planConfig, "warmup"),
          "warmup",
        ),
        ...segmentFlagsForMode(
          resolveSegmentMode(planConfig, "cooldown"),
          "cooldown",
        ),
        timeAvailable: planConfig.sessionDuration || 60,
        targetMuscles: planConfig.targetAreas || [],
        availableEquipment: Array.isArray(planConfig.availableEquipment)
          ? planConfig.availableEquipment.join(", ")
          : planConfig.availableEquipment || "none",
        avoidedAreas: planConfig.avoidedAreas || [],
        // Tested benchmarks (issue #18) — injected into the prompt so the model
        // can prescribe realistic loads/paces and annotate intensity targets.
        performanceCapabilities: performanceCapabilities || [],
        // Recent-history guidance (issue #9).
        recentTrainingHistory,
        historyLookbackDays,
        weeklyMuscleSets,
      };

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setCoachState((prev) => ({ ...prev, status: "planning" }));
      setError(null);
      setGenerationStatus("Building prompt...");
      setThinkingText(null);
      setLastGenerationUsage(null);

      // One generation can be several provider calls — a truncation retry or
      // the validation refinement pass each add one. Collect every call so the
      // cost we report is the cost of the whole generation.
      const usageEntries = [];
      // Which deterministic repairs fired and which validation issues survived,
      // per provider call. Reported so an unused repair pass can eventually be
      // deleted on evidence rather than argued about.
      const diagnosticEntries = [];
      // Ties the cost figure to the plan it produced, so the Session view only
      // shows it for the generated plan and not for a saved session loaded
      // afterwards.
      let generatedPlanId = null;
      // Tap to plan, recorded only when a plan arrives (#165): a cancelled or
      // failed run's time says nothing about how long the model takes.
      const startedAt = Date.now();
      let durationMs = null;

      try {
        const onStatus = (status) => setGenerationStatus(status);
        const onThinking = (text) => setThinkingText(text);
        const onUsage = (entry) => usageEntries.push(entry);
        const onDiagnostics = (entry) => diagnosticEntries.push(entry);
        // Wrapped, since useState treats a bare function as an updater.
        const onRefining = (skip) => setTakeFirstPlan(skip ? () => skip : null);

        const plan = await llmApi.generateWorkoutPlan(
          profile,
          llmSettings,
          2,
          controller.signal,
          onStatus,
          onThinking,
          onUsage,
          onDiagnostics,
          onRefining,
        );

        if (plan && plan.workout_plan) {
          durationMs = Date.now() - startedAt;
          setGenerationStatus("Loading workout...");
          // Bake the "guided by recent history" provenance onto the plan's meta
          // so the ActiveState pill survives a page reload (issue #26) — the
          // plan is persisted to localStorage, the old in-memory generationMeta
          // was not. Only stamped when history actually informed the session;
          // a subsequently loaded saved session has it stripped on load
          // (loadSessionFromData), preserving the issue #9 guarantee.
          const generatedPlan = plan.workout_plan;
          if (recentTrainingHistory.length > 0) {
            generatedPlan.meta = {
              ...(generatedPlan.meta || {}),
              guidance: {
                guided: true,
                lookbackDays: historyLookbackDays,
                sessionsConsidered: recentTrainingHistory.length,
                // One-line "why this session" rationale from the model; null
                // when omitted, in which case the chip shows its generic text.
                rationale: generatedPlan.meta?.historyNote || null,
              },
            };
          }
          // What Mixed turned into, so the session can say so rather than
          // leaving the user to guess whether anything varied at all.
          if (mixedResolution.archetype) {
            generatedPlan.meta = {
              ...(generatedPlan.meta || {}),
              mixedArchetype: {
                id: mixedResolution.archetype.id,
                name: mixedResolution.archetype.name,
                mode: mixedResolution.archetype.mode,
                reason: mixedResolution.archetype.reason,
                styles: mixedResolution.styles,
              },
            };
            rememberArchetypeId(mixedResolution.archetype.id);
          }
          generatedPlanId = generatedPlan.id ?? null;
          setLastGenerationConfig(
            buildCarryOverRecord(generatedPlanId, {
              ...planConfig,
              // Held across a regenerate: the same shape, different exercises.
              // A dialog opened clean has no held id and rotates instead.
              mixedArchetypeId: mixedResolution.archetype?.id || null,
            }),
          );
          setWorkoutPlan(generatedPlan);
          setSessionLoadedIntoCoach(false);
          setCoachState({
            status: "ready",
            timeLeftInPhase: 0,
            progress: [],
            canGoBack: false,
            isLast: false,
            totalSessionDuration: 0,
            elapsedSessionTime: 0,
            phaseStartElapsed: 0,
            setStartElapsed: 0,
            repPhaseCumulativeActual: 0,
            countUpMode: false,
          });
        } else {
          throw new Error("Received an invalid plan structure from the API.");
        }
      } catch (err) {
        if (err.name === "AbortError") {
          console.log("[WorkoutAI] Generation cancelled by user.");
          setError(null);
        } else {
          console.error("[WorkoutAI] Generate failed.", err);
          const description = describeGenerationError(err);
          setError(description);
          // A failure that got as far as a provider response still burned
          // tokens the user pays for, so say what it cost rather than letting
          // it disappear with the plan (issue #71).
          const spent = formatUsageSummary(accumulateUsage(usageEntries), money);
          // The Generate dialog auto-closes once we leave the "planning" state,
          // so without a toast the failure would vanish silently. Surface it,
          // and offer a one-tap retry with the same config.
          showToast({
            title: "Session generation failed",
            description: spent ? `${description} (spent ${spent})` : description,
            duration: 10000,
            actions: [
              {
                label: "Try again",
                onClick: () => generatePlan(planConfig),
              },
              // Or skip the plan and train anyway (#146). Through a ref:
              // loadPresetIntoCoach is declared further down.
              {
                label: "Just train",
                onClick: () => loadPresetRef.current?.("free"),
              },
            ],
          });
        }
        setCoachState((prev) => ({ ...prev, status: "ready" }));
      } finally {
        setGenerationStatus(null);
        setThinkingText(null);
        setTakeFirstPlan(null);
        // Set even on failure — a generation that errored after burning tokens
        // still cost the user money, and hiding that would be dishonest.
        const usage = accumulateUsage(usageEntries);
        setLastGenerationUsage(usage ? { ...usage, planId: generatedPlanId } : null);
        // Fire-and-forget, and recorded even when the generation failed — a
        // run that burned two calls and still could not clear its blockers is
        // the most informative row in the table.
        // One record per generation for the Usage report, failed ones too.
        // A failed run keeps its wait too (#167), so a slow failure shows as
        // slow in the Usage report rather than with no time at all.
        recordUsage({
          operation: "session",
          settings: llmSettings,
          entries: usageEntries,
          durationMs: durationMs ?? Date.now() - startedAt,
          ok: durationMs != null,
        });
        const diagnostics = accumulateDiagnostics(diagnosticEntries);
        if (diagnostics) {
          api
            .recordGenerationDiagnostics({ ...diagnostics, durationMs })
            .catch(() => {});
        }
        abortControllerRef.current = null;
      }
    },
    [
      llmSettings,
      money,
      locationPresets,
      performanceCapabilities,
      sessions,
      setWorkoutPlan,
      setLastGenerationConfig,
      setCoachState,
      setSessionLoadedIntoCoach,
      userProfile.fitnessLevel,
      userProfile.trainingExperience,
      userProfile.injuryNotes,
      userProfile.birthYear,
      userProfile.restStyle,
      userProfile.sessionPace,
      userProfile.trainingPreferences,
      userProfile.excludedSessionShapes,
      showToast,
    ],
  );

  const quickGenerate = useCallback(async () => {
    const planConfig = {
      primaryGoal: userProfile.primaryGoal || "General Fitness",
      targetAreas: userProfile.targetMuscles || [],
      sessionDuration: userProfile.timeAvailable || 45,
      workoutTypes: userProfile.workoutStyle
        ? [userProfile.workoutStyle]
        : ["mixed"],
      // Warm-up / cool-down tri-state from the saved profile (issue #70).
      ...segmentFlagsForMode(
        resolveSegmentMode(userProfile, "warmup"),
        "warmup",
      ),
      ...segmentFlagsForMode(
        resolveSegmentMode(userProfile, "cooldown"),
        "cooldown",
      ),
      autoWarmupThreshold: userProfile.autoWarmupThreshold ?? 30,
      autoCooldownThreshold: userProfile.autoCooldownThreshold ?? 30,
      warmupPreferredDuration: userProfile.warmupPreferredDuration ?? 0,
      cooldownPreferredDuration: userProfile.cooldownPreferredDuration ?? 0,
      warmupDuration: userProfile.warmupDuration || 5,
      cooldownDuration: userProfile.cooldownDuration || 5,
      availableEquipment: userProfile.availableEquipment || [],
      trainingLocation: userProfile.trainingLocation || "Home",
    };

    // Your default recipe (issue #78) is what "quick" should mean — otherwise
    // the one path that asks you nothing is also the one path that ignores the
    // session shape you told us you usually want. Sparse by construction, so
    // whatever the recipe does not pin still comes from the profile above.
    if (defaultRecipe) {
      Object.assign(
        planConfig,
        expandRecipeConfig(defaultRecipe, equipmentPresets),
      );
      touchRecipe(defaultRecipe.id);
    }

    await generatePlan(planConfig);
  }, [
    generatePlan,
    userProfile,
    defaultRecipe,
    equipmentPresets,
    touchRecipe,
  ]);

  const cancelGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Load a Quick preset (Tabata, HIIT, EMOM, AMRAP, Stopwatch, etc.) into the
  // unified guided engine. buildStandaloneSessionPlan() converts the preset to
  // a WorkoutNode tree, which is then loaded just like a guided session.
  const loadPresetIntoCoach = useCallback(
    (mode, config) => {
      // Normalise presets too (they bypass the localStorage mount coercion):
      // strips logistical ghost rests between different exercises while
      // preserving round-boundary + same-exercise interval rests.
      const plan = coerceLegacyNodeTypes(
        buildStandaloneSessionPlan(mode, config || {}),
      );
      if (!plan) return;
      clearLoadedSession();
      setWorkoutPlan(plan);
      setCoachState((prev) => ({
        ...prev,
        countUpMode: !!plan.countUpMode,
      }));
      setSessionLoadedIntoCoach(true);
      setView("coach");
    },
    [
      setWorkoutPlan,
      clearLoadedSession,
      setCoachState,
      setSessionLoadedIntoCoach,
      setView,
    ],
  );
  loadPresetRef.current = loadPresetIntoCoach;

  // ---------------------------------------------------------------------------
  // CONTEXT PROJECTIONS
  // ---------------------------------------------------------------------------
  // coachState is a single useState object that ticks every second
  // (timeLeftInPhase, elapsedSessionTime).  By pulling out individual fields
  // whose *references* are stable between ticks, useMemo skips recomputation
  // for the slow-moving AppStateContext while TimerContext re-evaluates each
  // second as expected.
  // ---------------------------------------------------------------------------

  // --- Slow-moving coach metadata (stable between ticks) ---
  const coachStateForApp = useMemo(
    () => ({
      status: coachState.status,
      currentNode: coachState.currentNode,
      nextUpNode: coachState.nextUpNode,
      path: coachState.path,
      loopCounters: coachState.loopCounters,
      progress: coachState.progress,
      canGoBack: coachState.canGoBack,
      isLast: coachState.isLast,
      totalSessionDuration: coachState.totalSessionDuration,
    }),
    [
      coachState.status,
      coachState.currentNode,
      coachState.nextUpNode,
      coachState.path,
      coachState.loopCounters,
      coachState.progress,
      coachState.canGoBack,
      coachState.isLast,
      coachState.totalSessionDuration,
    ],
  );

  // --- AppStateContext: everything EXCEPT per-second tick values ---
  const appValue = useMemo(
    () => ({
      // State
      userProfile,
      workoutPlan,
      appState,
      view,
      planSubView,
      settingsSheet,
      socialTab,
      isImmersive,
      error,
      coachState: coachStateForApp,
      sessionLoadedIntoCoach,
      countInTotal,
      generationStatus,
      thinkingText,
      takeFirstPlan,
      lastGenerationUsage,
      lastGenerationConfig,

      // Setters & Logic
      setUserProfile: handleSetUserProfile,
      notePeakHeartRate,
      setWorkoutPlan,
      setView,
      setPlanSubView,
      setSettingsSheet,
      setSocialTab,
      setImmersive: setIsImmersive,
      generatePlan,
      quickGenerate,
      cancelGeneration,

      // LLM
      llmSettings,
      setLlmSettings,
      money,

      // Coach Controls
      startWorkout,
      finishWorkout,
      completeWorkout,
      keepGoing,
      savePartialSession,
      playPause,
      skipToNext,
      skipToPrevious,
      confirmReady,
      skipRoundForward,
      skipRoundBack,
      getUpcomingExercises,
      getActiveLoadContext,
      updateActiveLoad,
      getLastRepsSet,
      getDoneRepsSets,
      amendSetReps,
      updateFreeLog,
      updateFreeChecklist,
      updateFreeRests,
      applySessionEdit,
      getTrimContext,
      loadSessionIntoCoach,
      loadSessionFromData,
      loadPresetIntoCoach,
      clearLoadedSession,
      clearSession,
      setActiveProgrammeSession,
      activeProgrammeSessionRef,
      updatePlanNode,
      deletePlanNode,
      addPlanNode,
      movePlanNode,
      duplicatePlanNode,
      planDisplayProps,

      // Device telemetry refs (read on session save)
      bikeSummaryRef,
      machinesRef,
      telemetryRef,
      telemetrySummaryRef,
      telemetrySessionIdRef,
      telemetryFinalizeRef,
      telemetryClearAfterSaveRef,
      telemetryPeekRef,
    }),
    [
      userProfile,
      workoutPlan,
      appState,
      view,
      planSubView,
      settingsSheet,
      socialTab,
      isImmersive,
      error,
      coachStateForApp,
      sessionLoadedIntoCoach,
      countInTotal,
      generationStatus,
      thinkingText,
      takeFirstPlan,
      lastGenerationUsage,
      lastGenerationConfig,
      handleSetUserProfile,
      notePeakHeartRate,
      setWorkoutPlan,
      setView,
      setPlanSubView,
      setIsImmersive,
      generatePlan,
      quickGenerate,
      cancelGeneration,
      llmSettings,
      setLlmSettings,
      money,
      startWorkout,
      finishWorkout,
      completeWorkout,
      keepGoing,
      savePartialSession,
      playPause,
      skipToNext,
      skipToPrevious,
      confirmReady,
      skipRoundForward,
      skipRoundBack,
      getUpcomingExercises,
      getActiveLoadContext,
      updateActiveLoad,
      getLastRepsSet,
      getDoneRepsSets,
      amendSetReps,
      updateFreeLog,
      updateFreeChecklist,
      updateFreeRests,
      applySessionEdit,
      getTrimContext,
      loadSessionIntoCoach,
      loadSessionFromData,
      loadPresetIntoCoach,
      clearLoadedSession,
      clearSession,
      setActiveProgrammeSession,
      updatePlanNode,
      deletePlanNode,
      addPlanNode,
      movePlanNode,
      duplicatePlanNode,
      planDisplayProps,
      bikeSummaryRef,
      machinesRef,
      telemetryRef,
      telemetrySummaryRef,
      telemetrySessionIdRef,
      telemetryFinalizeRef,
      telemetryClearAfterSaveRef,
      telemetryPeekRef,
    ],
  );

  // --- TimerContext: high-frequency tick data ---
  const timerValue = useMemo(
    () => ({
      coachTick: {
        timeLeftInPhase: coachState.timeLeftInPhase,
        elapsedSessionTime: coachState.elapsedSessionTime,
      },
      countInRemaining,
      countInContext,
    }),
    [
      coachState.timeLeftInPhase,
      coachState.elapsedSessionTime,
      countInRemaining,
      countInContext,
    ],
  );

  // --- Legacy WorkoutContext bridge (all consumers not yet migrated) ---
  // This value changes every second — same perf profile as before the split.
  const legacyValue = useMemo(
    () => ({
      ...appValue,
      // Override coachState with the full object (includes tick fields)
      coachState,
    }),
    [appValue, coachState],
  );

  // Plain formatters read the weight unit from here (#191); set it before
  // the children render so they all agree.
  const weightUnit = weightUnitOf(userProfile);
  setShownWeightUnit(weightUnit);

  const hrZoneFloors = useMemo(() => {
    const basis = heartRateBasis(userProfile);
    return basis ? zoneFloors(basis) : null;
  }, [userProfile]);

  return (
    <AppStateContext.Provider value={appValue}>
      <TimerContext.Provider value={timerValue}>
        <WorkoutContext.Provider value={legacyValue}>
          <ZoneFloorsContext.Provider value={hrZoneFloors}>
            <WeightUnitContext.Provider value={weightUnit}>{children}</WeightUnitContext.Provider>
          </ZoneFloorsContext.Provider>
        </WorkoutContext.Provider>
      </TimerContext.Provider>
    </AppStateContext.Provider>
  );
};
